import React, { useState, useRef } from 'react';
import { Vote, Shield, Users, Trash2, Plus, Loader2, AlertCircle, Calendar, Award, Camera, Upload, Edit, Settings, X } from 'lucide-react';
import { useVotacao } from '../hooks/useVotacao';
import { useParametrosPerfil } from '../hooks/useParametrosPerfil';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../config/supabase';
import ValidacaoEleitores from '../components/ValidacaoEleitores';
import { generateEmpresaSlug } from '../utils/slugUtils';

const VotacaoTab: React.FC = () => {
  const { user, empresaSelecionada } = useAuth();
  const { parametros, podeAdicionarEleitor, podeTerFuncionarios, getLimiteEleitores, getLimiteFuncionarios, getEleitoresRestantes } = useParametrosPerfil(user?.tipo_usuario || '');
  const {
    candidatos,
    eleicoes,
    eleicaoAtiva,
    loading,
    error,
    adicionarCandidato,
    removerCandidato,
    atualizarFotoCandidato,
    obterFotoCandidato,
    criarEleicao,
    iniciarEleicao,
    encerrarEleicao,
    recarregarCandidatos,
    limparCandidatosDuplicados,
    realizarApuracao,
    gerarRelatorioCSV,
    gerarRelatorioCandidatos
  } = useVotacao();

  // Estados locais
  const [mostrarCadastroCandidatos, setMostrarCadastroCandidatos] = useState(false);
  const [novoCandidato, setNovoCandidato] = useState({ nome: '', matricula: '', setor: '' });
  const [fotoCandidato, setFotoCandidato] = useState<File | null>(null);
  const [previewFoto, setPreviewFoto] = useState<string | null>(null);
  const [senhaSistema, setSenhaSistema] = useState('');
  const [tituloEleicao, setTituloEleicao] = useState('Eleição CIPA - Gestão 2025/2026');
  const [nomeEmpresa, setNomeEmpresa] = useState('');
  const [slugEmpresa, setSlugEmpresa] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Nova variável para eleição em configuração (status: 'configurando')
  const [eleicaoConfiguração, setEleicaoConfiguracao] = useState<any>(null);
  const lastVotosHistoricoRef = useRef<string>('');
  const lastEleitoresRef = useRef<string>('');

  // Estados para contagem de votos do histórico
  const [votosHistorico, setVotosHistorico] = useState<{[key: string]: number}>({});

  // Estados para upload de CSV de eleitores
  const [arquivoCsv, setArquivoCsv] = useState<File | null>(null);
  const [eleitoresAutorizados, setEleitoresAutorizados] = useState<any[]>([]);
  const [processandoCsv, setProcessandoCsv] = useState(false);
  const [carregandoEleitores, setCarregandoEleitores] = useState(false);

  // Estados para dados da empresa
  const [dadosEmpresa, setDadosEmpresa] = useState<{
    nome: string;
    grauRisco: number;
    numeroFuncionarios: number;
  } | null>(null);

  // Estados para apuração
  const [resultadoApuracao, setResultadoApuracao] = useState<any>(null);
  const [mostrarModalApuracao, setMostrarModalApuracao] = useState(false);
  const [processandoApuracao, setProcessandoApuracao] = useState(false);

  // Estado para controle do cadastro público
  const [cadastroPublicoHabilitado, setCadastroPublicoHabilitado] = useState(false);

  // Estados para votação (removidos - votação feita na página pública)

  // Função para lidar com seleção de foto
  const handleFotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validar tipo de arquivo
      if (!file.type.startsWith('image/')) {
        alert('Por favor, selecione apenas arquivos de imagem (JPG, PNG, etc.)');
        return;
      }

      // Validar tamanho (máximo 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('A foto deve ter no máximo 5MB');
        return;
      }

      setFotoCandidato(file);

      // Criar preview
      const reader = new FileReader();
      reader.onload = () => {
        setPreviewFoto(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Função para remover foto
  const handleRemoverFoto = () => {
    setFotoCandidato(null);
    setPreviewFoto(null);
    // Limpar o input file
    const fileInput = document.getElementById('foto-candidato') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  // Função para adicionar candidato
  const handleAdicionarCandidato = async () => {
    if (!novoCandidato.nome || !novoCandidato.matricula || !novoCandidato.setor) {
      alert('Preencha todos os campos obrigatórios do candidato!');
      return;
    }

    try {
      setSubmitting(true);
      
      // Se é o primeiro candidato e não existe eleição em configuração, criar uma
      if (candidatos.length === 0 && !eleicaoConfiguração) {
        await criarEleicaoConfiguracao();
      }
      
      await adicionarCandidato(novoCandidato.nome, novoCandidato.matricula, novoCandidato.setor, fotoCandidato || undefined);
      setNovoCandidato({ nome: '', matricula: '', setor: '' });
      setFotoCandidato(null);
      setPreviewFoto(null);
      alert('✅ Candidato adicionado com sucesso!');
    } catch (err) {
      alert('❌ Erro ao adicionar candidato. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  // Função para remover candidato
  const handleRemoverCandidato = async (candidatoId: string) => {
    if (window.confirm('Tem certeza que deseja remover este candidato?')) {
      try {
        await removerCandidato(candidatoId);
        alert('Candidato removido com sucesso!');
      } catch (err) {
        alert('Erro ao remover candidato. Tente novamente.');
      }
    }
  };

  // Função para atualizar foto do candidato
  const handleAtualizarFoto = async (candidatoId: string) => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*';
    fileInput.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        try {
          setSubmitting(true);
          await atualizarFotoCandidato(candidatoId, file);
          alert('Foto atualizada com sucesso!');
        } catch (err) {
          alert('Erro ao atualizar foto. Tente novamente.');
        } finally {
          setSubmitting(false);
        }
      }
    };
    fileInput.click();
  };

  // Função para iniciar votação
  const handleIniciarVotacao = async () => {
    // Validações obrigatórias
    if (candidatos.length < 2) {
      alert('❌ Cadastre pelo menos 2 candidatos para iniciar a votação!');
      return;
    }

    if (!senhaSistema) {
      alert('❌ Defina uma senha para o sistema de votação!');
      return;
    }

    if (!tituloEleicao) {
      alert('❌ Defina um título para a eleição!');
      return;
    }

    if (!nomeEmpresa) {
      alert('❌ Defina o nome da empresa!');
      return;
    }

    if (!slugEmpresa) {
      alert('❌ Defina o slug da empresa para a URL!');
      return;
    }

    // NOVA VALIDAÇÃO OBRIGATÓRIA: Verificar se existem eleitores cadastrados
    if (eleitoresAutorizados.length === 0) {
      alert('❌ ERRO: Nenhum eleitor autorizado foi cadastrado!\n\n' +
            'Para iniciar a votação é obrigatório:\n' +
            '1. Fazer upload de um arquivo CSV com os CPFs dos eleitores autorizados\n' +
            '2. Processar o arquivo com sucesso\n\n' +
            'Sem essa lista, o sistema não conseguirá validar os votos.');
      return;
    }

    // VALIDAÇÃO CRÍTICA: Verificar se o número de eleitores é exatamente igual ao número de funcionários
    if (dadosEmpresa && eleitoresAutorizados.length !== dadosEmpresa.numeroFuncionarios) {
      const diferenca = eleitoresAutorizados.length - dadosEmpresa.numeroFuncionarios;
      const mensagem = diferenca > 0 
        ? `❌ ERRO: Número de eleitores EXCEDE funcionários da empresa!\n\nFuncionários cadastrados na empresa ${dadosEmpresa.nome}: ${dadosEmpresa.numeroFuncionarios}\n\nEleitores cadastrados: ${eleitoresAutorizados.length}\n\nExcesso: ${diferenca} eleitores\n\nPara iniciar a votação, o número de eleitores deve ser EXATAMENTE igual ao número de funcionários cadastrados na empresa.`
        : `❌ ERRO: Número de eleitores é MENOR que funcionários da empresa!\n\nFuncionários cadastrados na empresa ${dadosEmpresa.nome}: ${dadosEmpresa.numeroFuncionarios}\n\nEleitores cadastrados: ${eleitoresAutorizados.length}\n\nFaltam: ${Math.abs(diferenca)} eleitores\n\nPara iniciar a votação, o número de eleitores deve ser EXATAMENTE igual ao número de funcionários cadastrados na empresa.`;
      
      alert(mensagem);
      return;
    }

    // Confirmação final com resumo detalhado
    const mensagemConfirmacao = 
      `🗳️ CONFIRMAR INÍCIO DA VOTAÇÃO?\n\n` +
      `📋 RESUMO DA ELEIÇÃO:\n` +
      `• Título: ${tituloEleicao}\n` +
      `• Empresa: ${nomeEmpresa}\n` +
      `• URL Pública: https://cipa.ehsfacil.com/${slugEmpresa}\n\n` +
      `👥 CANDIDATOS (${candidatos.length}):\n` +
      candidatos.map(c => `• ${c.nome} (Mat. ${c.matricula} - ${c.setor})`).join('\n') + '\n\n' +
      `🗳️ ELEITORES (${eleitoresAutorizados.length}):\n` +
      `• Total de eleitores autorizados: ${eleitoresAutorizados.length}\n` +
      `• Funcionários da empresa: ${dadosEmpresa?.numeroFuncionarios}\n` +
      `• Status: ✅ VALIDAÇÃO APROVADA\n\n` +
      `⚠️ ATENÇÃO:\n` +
      `• Após iniciar, NÃO será possível adicionar/remover candidatos\n` +
      `• A votação ficará ativa até ser encerrada manualmente\n` +
      `• Apenas será possível encerrar a votação (não reabrir)\n\n` +
      `❓ Tem certeza que deseja INICIAR a votação agora?`;

    const confirmar = window.confirm(mensagemConfirmacao);

    if (!confirmar) return;

    try {
      setSubmitting(true);
      
      let eleicaoFinal;
      
      if (eleicaoConfiguração) {
        // Atualizar eleição em configuração com dados finais
        const { error: updateError } = await supabase
          .from('cp_eleicoes')
          .update({
            titulo: tituloEleicao,
            senha_votacao: senhaSistema,
            empresa_nome: nomeEmpresa,
            empresa_slug: slugEmpresa
          })
          .eq('id', eleicaoConfiguração.id);

        if (updateError) {
          throw updateError;
        }

        eleicaoFinal = eleicaoConfiguração;
      } else {
        // Criar nova eleição se não existe configuração
        eleicaoFinal = await criarEleicao(tituloEleicao, senhaSistema, nomeEmpresa, slugEmpresa);
      }
      
      // Ativar a eleição
      await iniciarEleicao(eleicaoFinal.id);
      
      // Limpar estado de configuração
      setEleicaoConfiguracao(null);
      setMostrarCadastroCandidatos(false);
      
      alert('🎉 Votação iniciada com sucesso!\n\n' +
            `📊 ${candidatos.length} candidatos\n` +
            `👥 ${eleitoresAutorizados.length} eleitores autorizados\n\n` +
            `🌐 URL pública: https://cipa.ehsfacil.com/${slugEmpresa}`);
    } catch (err) {
      alert('❌ Erro ao iniciar votação. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  // Função para encerrar votação
  const handleEncerrarVotacao = async () => {
    if (!eleicaoAtiva) return;

    // Validar se atingiu 51% dos votos
    const totalVotos = eleicaoAtiva.total_votos || 0;
    const totalEleitores = eleitoresAutorizados.length;
    const percentualVotos = totalEleitores > 0 ? (totalVotos / totalEleitores) * 100 : 0;
    const percentualMinimo = 51;

    if (percentualVotos < percentualMinimo) {
      alert(`❌ Não é possível encerrar a votação!\n\n` +
            `📊 Estatísticas atuais:\n` +
            `• Total de eleitores: ${totalEleitores}\n` +
            `• Total de votos: ${totalVotos}\n` +
            `• Percentual de participação: ${percentualVotos.toFixed(1)}%\n\n` +
            `⚠️ É necessário atingir pelo menos ${percentualMinimo}% de participação para encerrar a votação.\n\n` +
            `📈 Faltam ${Math.ceil((percentualMinimo / 100) * totalEleitores) - totalVotos} votos para atingir o mínimo.`);
      return;
    }

    const confirmar = window.confirm(
      `🗳️ CONFIRMAR ENCERRAMENTO DA VOTAÇÃO?\n\n` +
      `📊 Estatísticas finais:\n` +
      `• Total de eleitores: ${totalEleitores}\n` +
      `• Total de votos: ${totalVotos}\n` +
      `• Percentual de participação: ${percentualVotos.toFixed(1)}%\n\n` +
      `✅ A votação atingiu o mínimo de ${percentualMinimo}% de participação.\n\n` +
      `Após encerrar, não será possível reabrir a votação.`
    );

    if (confirmar) {
      try {
        setProcessandoApuracao(true);
        
        // Encerrar a eleição
        await encerrarEleicao(eleicaoAtiva.id);
        
        // Realizar apuração automática
        console.log('📊 Iniciando apuração automática...');
        const resultado = await realizarApuracao(eleicaoAtiva.id);
        
        if (resultado) {
          setResultadoApuracao(resultado);
          setMostrarModalApuracao(true);
        } else {
          alert('✅ Votação encerrada com sucesso!');
        }
        
      } catch (err) {
        console.error('❌ Erro ao encerrar votação:', err);
        alert('❌ Erro ao encerrar votação. Tente novamente.');
      } finally {
        setProcessandoApuracao(false);
      }
    }
  };

  // Função para carregar dados da empresa
  const carregarDadosEmpresa = async () => {
    try {
      console.log('🔍 Carregando dados da empresa...');
      console.log('🔍 Dados completos do usuário:', user);
      console.log('🔍 Empresa selecionada:', empresaSelecionada);
      
      if (!empresaSelecionada) {
        console.log('❌ Nenhuma empresa selecionada');
        return;
      }

      // Buscar empresa pelo ID da empresa selecionada (força refresh sem cache)
      const { data: empresa, error } = await supabase
        .from('cp_empresas')
        .select('nome, grau_risco, numero_funcionarios')
        .eq('id', empresaSelecionada.id)
        .single();

      console.log('🔍 Resultado da consulta:', { empresa, error });
      console.log('🔍 Número de funcionários na consulta:', empresa?.numero_funcionarios);

      if (error) {
        console.error('Erro ao carregar dados da empresa:', error);
        return;
      }

      if (empresa) {
        const dadosEmpresa = {
          nome: empresa.nome,
          grauRisco: empresa.grau_risco || 2,
          numeroFuncionarios: empresa.numero_funcionarios || 0
        };
        console.log('✅ Dados da empresa carregados:', dadosEmpresa);
        console.log('🔍 Dados brutos da consulta:', empresa);
        console.log('🔍 Número de funcionários processado:', dadosEmpresa.numeroFuncionarios);
        setDadosEmpresa(dadosEmpresa);
      } else {
        console.log('❌ Nenhuma empresa encontrada com o ID:', empresaSelecionada.id);
      }
    } catch (error) {
      console.error('Erro ao carregar dados da empresa:', error);
    }
  };

  // Função para carregar eleitores autorizados existentes
  const carregarEleitoresExistentes = async () => {
    const eleicaoParaUsar = eleicaoAtiva || eleicaoConfiguração;
    
    if (!eleicaoParaUsar) {
      setEleitoresAutorizados([]);
      return;
    }

    try {
      setCarregandoEleitores(true);

      const { data: eleitoresBanco, error } = await supabase
        .from('cp_eleitores_autorizados')
        .select('cpf, nome, setor')
        .eq('eleicao_id', eleicaoParaUsar.id)
        .order('nome');

      if (error) {
        throw error;
      }

      setEleitoresAutorizados(eleitoresBanco || []);

    } catch (error) {
      setEleitoresAutorizados([]);
    } finally {
      setCarregandoEleitores(false);
    }
  };

  // Função para carregar candidatos existentes de uma eleição em configuração
  const carregarCandidatosExistentes = async () => {
    if (!eleicaoConfiguração) return;

    try {
      console.log('🔄 Carregando candidatos existentes da eleição:', eleicaoConfiguração.id);
      
      // Usar a função do hook para recarregar candidatos
      await recarregarCandidatos(eleicaoConfiguração.id);
      
      console.log('✅ Candidatos existentes recarregados');

    } catch (error) {
      console.error('❌ Erro ao carregar candidatos existentes:', error);
    }
  };

  // Função para editar eleição em configuração
  const editarEleicaoConfiguracao = async (eleicao: any) => {
    try {
      console.log('🔄 Editando eleição em configuração:', eleicao.id);
      
      // Carregar dados da eleição para edição
      setEleicaoConfiguracao(eleicao);
      setTituloEleicao(eleicao.titulo || 'Eleição CIPA - Gestão 2025/2026');
      setNomeEmpresa(eleicao.empresa_nome || '');
      
      // Gerar slug automaticamente baseado no nome da empresa
      const nomeEmpresaAtual = eleicao.empresa_nome || '';
      if (nomeEmpresaAtual) {
        const novoSlug = generateEmpresaSlug(nomeEmpresaAtual);
        setSlugEmpresa(novoSlug);
      } else {
        setSlugEmpresa(eleicao.empresa_slug || '');
      }
      
      setSenhaSistema(eleicao.senha_votacao || '');
      
      // Carregar candidatos existentes
      await recarregarCandidatos(eleicao.id);
      
      // Carregar eleitores existentes
      await carregarEleitoresExistentes();
      
      // Mostrar tela de configuração
      setMostrarCadastroCandidatos(true);
      
      console.log('✅ Eleição carregada para edição');
      
    } catch (error) {
      console.error('❌ Erro ao editar eleição:', error);
      alert('Erro ao carregar eleição para edição. Tente novamente.');
    }
  };

  // Função para excluir eleição em configuração
  const excluirEleicaoConfiguracao = async (eleicao: any) => {
    if (!window.confirm(`Tem certeza que deseja excluir a eleição "${eleicao.titulo}"?\n\nEsta ação não pode ser desfeita e todos os candidatos e eleitores cadastrados serão removidos.`)) {
      return;
    }

    try {
      console.log('🗑️ Excluindo eleição em configuração:', eleicao.id);
      
      // Excluir candidatos da eleição
      const { error: candidatosError } = await supabase
        .from('cp_candidatos')
        .delete()
        .eq('eleicao_id', eleicao.id);

      if (candidatosError) {
        console.error('❌ Erro ao excluir candidatos:', candidatosError);
        throw candidatosError;
      }

      // Excluir eleitores autorizados da eleição
      const { error: eleitoresError } = await supabase
        .from('cp_eleitores_autorizados')
        .delete()
        .eq('eleicao_id', eleicao.id);

      if (eleitoresError) {
        console.error('❌ Erro ao excluir eleitores:', eleitoresError);
        throw eleitoresError;
      }

      // Excluir a eleição
      const { error: eleicaoError } = await supabase
        .from('cp_eleicoes')
        .delete()
        .eq('id', eleicao.id);

      if (eleicaoError) {
        console.error('❌ Erro ao excluir eleição:', eleicaoError);
        throw eleicaoError;
      }

      // Limpar estado se era a eleição em configuração atual
      if (eleicaoConfiguração && eleicaoConfiguração.id === eleicao.id) {
        setEleicaoConfiguracao(null);
        setMostrarCadastroCandidatos(false);
        setTituloEleicao('Eleição CIPA - Gestão 2025/2026');
        setNomeEmpresa('');
        setSlugEmpresa('');
        setSenhaSistema('');
      }

      // Recarregar eleições
      await recarregarCandidatos();
      
      alert('✅ Eleição excluída com sucesso!');
      
    } catch (error) {
      console.error('❌ Erro ao excluir eleição:', error);
      alert('Erro ao excluir eleição. Tente novamente.');
    }
  };

  // Função para limpar candidatos duplicados
  const handleLimparCandidatosDuplicados = async () => {
    if (!window.confirm('Tem certeza que deseja limpar candidatos duplicados?\n\nEsta ação irá remover candidatos com a mesma matrícula, mantendo apenas o mais recente.')) {
      return;
    }

    try {
      setSubmitting(true);
      await limparCandidatosDuplicados();
      await recarregarCandidatos();
      alert('✅ Candidatos duplicados removidos com sucesso!');
    } catch (error) {
      console.error('❌ Erro ao limpar candidatos duplicados:', error);
      alert('Erro ao limpar candidatos duplicados. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  // Função para fechar modal de apuração
  const fecharModalApuracao = () => {
    setMostrarModalApuracao(false);
    setResultadoApuracao(null);
  };

  // Função para baixar relatório CSV
  const handleBaixarRelatorio = () => {
    if (resultadoApuracao) {
      gerarRelatorioCSV(resultadoApuracao);
    }
  };

  // Função para baixar relatório de candidatos
  const handleBaixarRelatorioCandidatos = async (eleicao: any) => {
    try {
      await gerarRelatorioCandidatos(eleicao.id);
    } catch (error) {
      console.error('❌ Erro ao baixar relatório de candidatos:', error);
      alert('Erro ao baixar relatório de candidatos. Tente novamente.');
    }
  };

  // Função para buscar eleições ativas recentes
  const buscarEleicoesAtivas = async () => {
    try {
      console.log('🔍 [BUSCAR ELEIÇÕES] Buscando eleições ativas...');
      
      const { data: eleicoes, error } = await supabase
        .from('cp_eleicoes')
        .select('id, titulo, empresa_nome, empresa_slug, status, cadastro_publico_habilitado')
        .eq('status', 'ativa')
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) {
        console.error('❌ [BUSCAR ELEIÇÕES] Erro:', error);
        return null;
      }

      console.log('✅ [BUSCAR ELEIÇÕES] Eleições encontradas:', eleicoes);
      return eleicoes && eleicoes.length > 0 ? eleicoes[0] : null;
    } catch (error) {
      console.error('❌ [BUSCAR ELEIÇÕES] Erro inesperado:', error);
      return null;
    }
  };

  // Função para alternar cadastro público
  const handleToggleCadastroPublico = async () => {
    let eleicaoParaUsar = eleicaoAtiva || eleicaoConfiguração;
    
    console.log('🔄 [CADASTRO PÚBLICO] Iniciando toggle...');
    console.log('🔄 [CADASTRO PÚBLICO] Eleição para usar:', eleicaoParaUsar);
    console.log('🔄 [CADASTRO PÚBLICO] Estado atual:', cadastroPublicoHabilitado);
    
    // Se não há eleição local, tentar buscar no banco
    if (!eleicaoParaUsar) {
      console.log('🔍 [CADASTRO PÚBLICO] Nenhuma eleição local, buscando no banco...');
      eleicaoParaUsar = await buscarEleicoesAtivas();
      
      if (!eleicaoParaUsar) {
        console.log('❌ [CADASTRO PÚBLICO] Nenhuma eleição encontrada');
        alert('❌ Erro: Nenhuma eleição encontrada. Crie uma eleição primeiro.');
        return;
      }
    }

    try {
      const novoEstado = !cadastroPublicoHabilitado;
      console.log('🔄 [CADASTRO PÚBLICO] Novo estado:', novoEstado);
      
      // Atualizar no banco de dados
      console.log('🔄 [CADASTRO PÚBLICO] Atualizando no banco...');
      const { data, error } = await supabase
        .from('cp_eleicoes')
        .update({ cadastro_publico_habilitado: novoEstado })
        .eq('id', eleicaoParaUsar.id)
        .select();

      console.log('🔄 [CADASTRO PÚBLICO] Resultado da atualização:', { data, error });

      if (error) {
        console.error('❌ [CADASTRO PÚBLICO] Erro no banco:', error);
        throw error;
      }

      setCadastroPublicoHabilitado(novoEstado);
      console.log('✅ [CADASTRO PÚBLICO] Estado atualizado localmente');
      
      const mensagem = novoEstado 
        ? '✅ Cadastro público habilitado! Os candidatos podem se cadastrar usando o link público.'
        : '🔒 Cadastro público desabilitado! Apenas o administrador pode adicionar candidatos.';
      
      alert(mensagem);

    } catch (error) {
      console.error('❌ [CADASTRO PÚBLICO] Erro ao alterar cadastro público:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      alert(`❌ Erro ao alterar configuração do cadastro público: ${errorMessage}`);
    }
  };

  // Função para visualizar apuração de eleição encerrada
  const handleVisualizarApuracao = async (eleicao: any) => {
    try {
      setProcessandoApuracao(true);
      console.log('📊 Carregando apuração da eleição:', eleicao.id);
      
      const resultado = await realizarApuracao(eleicao.id);
      
      if (resultado) {
        setResultadoApuracao(resultado);
        setMostrarModalApuracao(true);
      } else {
        alert('❌ Não foi possível carregar a apuração desta eleição.');
      }
      
    } catch (error) {
      console.error('❌ Erro ao carregar apuração:', error);
      alert('❌ Erro ao carregar apuração. Tente novamente.');
    } finally {
      setProcessandoApuracao(false);
    }
  };

  // Função para criar eleição em modo configuração
  const criarEleicaoConfiguracao = async () => {
    if (eleicaoConfiguração) {
      return eleicaoConfiguração;
    }

    try {
      const tituloTemp = tituloEleicao || 'Eleição CIPA - Em Configuração';
      const nomeEmpresaTemp = nomeEmpresa || 'Empresa (Em Configuração)';
      // Gerar slug automaticamente se não existir
      const slugEmpresaTemp = slugEmpresa || generateEmpresaSlug(nomeEmpresaTemp);
      const senhaTemp = senhaSistema || 'temp123';

      // Criar eleição em modo 'configurando'
      const novaEleicao = await criarEleicao(tituloTemp, senhaTemp, nomeEmpresaTemp, slugEmpresaTemp);
      
      // Não iniciar ainda - manter em modo 'configurando'
      setEleicaoConfiguracao(novaEleicao);
      
      return novaEleicao;
      
    } catch (error) {
      throw error;
    }
  };

  // Função para criar eleição em modo configuração (nova)
  const criarEleicaoNova = async () => {
    try {
      const tituloTemp = tituloEleicao || 'Eleição CIPA - Gestão 2025/2026';
      const nomeEmpresaTemp = nomeEmpresa || 'Empresa';
      const slugEmpresaTemp = slugEmpresa || generateEmpresaSlug(nomeEmpresaTemp);
      const senhaTemp = senhaSistema || 'temp123';

      console.log('🚀 [CRIAR ELEIÇÃO] Iniciando criação...');
      console.log('🚀 [CRIAR ELEIÇÃO] Dados:', {
        titulo: tituloTemp,
        empresa: nomeEmpresaTemp,
        slug: slugEmpresaTemp,
        usuario_id: user?.id,
        empresa_id: empresaSelecionada?.id
      });

      // Validação: Verificar se os IDs existem
      if (!user?.id) {
        throw new Error('❌ Erro: Usuário não encontrado. Faça login novamente.');
      }

      if (!empresaSelecionada?.id) {
        throw new Error('❌ Erro: Empresa não selecionada. Selecione uma empresa antes de criar a eleição.');
      }

      // Criar eleição em modo configuração
      const { data: novaEleicao, error } = await supabase
        .from('cp_eleicoes')
        .insert({
          titulo: tituloTemp,
          senha_votacao: senhaTemp,
          empresa_nome: nomeEmpresaTemp,
          empresa_slug: slugEmpresaTemp,
          status: 'configurando', // Criar em modo configuração
          cadastro_publico_habilitado: true, // Habilitar cadastro público por padrão
          votacao_ativa: false, // Votação ainda não iniciada
          usuario_id: user.id, // ✅ ID do usuário (validado)
          empresa_id: empresaSelecionada.id // ✅ ID da empresa (validado)
        })
        .select()
        .single();

      if (error) {
        console.error('❌ [CRIAR ELEIÇÃO] Erro:', error);
        throw error;
      }

      console.log('✅ [CRIAR ELEIÇÃO] Eleição criada:', novaEleicao);
      console.log('✅ [CRIAR ELEIÇÃO] IDs verificados:', {
        usuario_id: novaEleicao.usuario_id,
        empresa_id: novaEleicao.empresa_id,
        status: novaEleicao.status
      });
      
      // Limpar estado de configuração
      setEleicaoConfiguracao(null);
      
      // Atualizar estado local para refletir a nova eleição
      console.log('🔄 [CRIAR ELEIÇÃO] Atualizando estado local...');
      
      // Atualizar estado do cadastro público
      setCadastroPublicoHabilitado(true);
      
      // Definir eleição em configuração
      setEleicaoConfiguracao(novaEleicao);
      
      console.log('✅ [CRIAR ELEIÇÃO] Estado atualizado localmente');
      console.log('✅ [CRIAR ELEIÇÃO] Eleição criada:', novaEleicao);
      
      return novaEleicao;
      
    } catch (error) {
      console.error('❌ [CRIAR ELEIÇÃO] Erro ao criar eleição:', error);
      throw error;
    }
  };

  // Função para registrar voto removida - votação feita na página pública

  // Função para lidar com seleção de arquivo CSV
  const handleCsvChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    
    if (file) {
      if (!file.name.toLowerCase().endsWith('.csv')) {
        alert('Por favor, selecione apenas arquivos CSV');
        return;
      }
      
      setArquivoCsv(file);
    }
  };

  // Função para processar CSV e extrair CPFs
  const processarCsv = async (): Promise<any[]> => {
    if (!arquivoCsv) {
      return [];
    }

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const csv = e.target?.result as string;
          
          if (!csv || csv.trim() === '') {
            reject(new Error('Arquivo CSV está vazio'));
            return;
          }
          
          const linhas = csv.split('\n').map(linha => linha.trim()).filter(linha => linha);
          
          if (linhas.length < 2) {
            reject(new Error('CSV deve ter pelo menos 2 linhas (cabeçalho + dados)'));
            return;
          }

          const eleitores: any[] = [];
          
          // Assumir primeira linha como cabeçalho
          const cabecalho = linhas[0].split(',').map(col => col.trim().toLowerCase());
          
          const indiceCpf = cabecalho.findIndex(col => col.includes('cpf'));
          const indiceNome = cabecalho.findIndex(col => col.includes('nome'));
          const indiceSetor = cabecalho.findIndex(col => col.includes('setor'));

          if (indiceCpf === -1) {
            reject(new Error('Coluna "CPF" não encontrada no CSV. Certifique-se de que a primeira linha contém "cpf" como cabeçalho.'));
            return;
          }

          // Processar dados (a partir da linha 2)
          for (let i = 1; i < linhas.length; i++) {
            const linha = linhas[i];
            const colunas = linha.split(',').map(col => col.trim());
            
            if (colunas.length > indiceCpf) {
              const cpfOriginal = colunas[indiceCpf];
              const cpf = cpfOriginal.replace(/\D/g, ''); // Remove tudo que não é número
              
              if (cpf.length === 11) {
                const eleitor = {
                  cpf,
                  nome: indiceNome >= 0 ? colunas[indiceNome] : '',
                  setor: indiceSetor >= 0 ? colunas[indiceSetor] : ''
                };
                eleitores.push(eleitor);
              }
            }
          }

          resolve(eleitores);
          
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Erro ao ler arquivo'));
      };
      
      reader.readAsText(arquivoCsv);
    });
  };

  // Função para salvar eleitores no banco com eleição específica
  const salvarEleitoresComEleicao = async (eleitores: any[], eleicao: any) => {
    if (!eleicao || !eleicao.id) {
      alert('Erro interno: Eleição inválida. Recarregue a página e tente novamente.');
      return;
    }

    try {
      setProcessandoCsv(true);

      // Limpar eleitores existentes desta eleição
      const { error: deleteError } = await supabase
        .from('cp_eleitores_autorizados')
        .delete()
        .eq('eleicao_id', eleicao.id);

      if (deleteError) {
        throw deleteError;
      }

      // Obter usuário atual
      let user = (window as any).supabaseUser;
      
      if (!user) {
        try {
          const authData = localStorage.getItem('supabase.auth.token');
          if (authData) {
            const parsedAuth = JSON.parse(authData);
            user = parsedAuth?.user;
          }
        } catch (e) {
          // Ignorar erro de parsing
        }
      }
      
      if (!user) {
        try {
          const { data: { user: currentUser } } = await supabase.auth.getUser();
          user = currentUser;
        } catch (e) {
          // Ignorar erro
        }
      }

      if (!user?.id) {
        throw new Error('Usuário não autenticado. Faça login novamente.');
      }

      // Inserir novos eleitores
      const eleitoresParaInserir = eleitores.map(eleitor => ({
        eleicao_id: eleicao.id,
        usuario_id: user.id,
        cpf: eleitor.cpf,
        nome: eleitor.nome,
        setor: eleitor.setor
      }));

      const { error: insertError } = await supabase
        .from('cp_eleitores_autorizados')
        .insert(eleitoresParaInserir);

      if (insertError) {
        throw insertError;
      }

      setEleitoresAutorizados(eleitores);
      alert(`✅ ${eleitores.length} eleitores autorizados cadastrados com sucesso!`);
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      alert('❌ Erro ao salvar eleitores: ' + errorMessage);
    } finally {
      setProcessandoCsv(false);
    }
  };



  // Função para processar e salvar CSV
  const handleProcessarCsv = async () => {
    if (!arquivoCsv) {
      alert('Selecione um arquivo CSV primeiro');
      return;
    }

    // Validar se os dados da empresa estão carregados
    if (!dadosEmpresa) {
      alert('❌ Erro: Dados da empresa não carregados!\n\nPor favor, aguarde o carregamento dos dados da empresa ou recarregue a página.');
      return;
    }

    // Verificar se existe eleição (ativa ou em configuração) ou criar uma nova
    let eleicaoParaUsar = eleicaoAtiva || eleicaoConfiguração;
    
    if (!eleicaoParaUsar) {
      try {
        eleicaoParaUsar = await criarEleicaoConfiguracao();
        
        // Garantir que o estado seja atualizado
        if (!eleicaoConfiguração) {
          setEleicaoConfiguracao(eleicaoParaUsar);
        }
      } catch (error) {
        alert('Erro ao preparar eleição. Tente novamente.');
        return;
      }
    }

    try {
      setProcessandoCsv(true);
      
      const eleitores = await processarCsv();
      
      if (eleitores.length === 0) {
        alert('❌ Nenhum CPF válido encontrado no arquivo.\n\nVerifique se:\n• O arquivo tem o formato correto\n• A primeira linha contém os cabeçalhos\n• Os CPFs têm 11 dígitos');
        return;
      }

      // Validar limite de funcionários baseado no número de eleitores
      if (!podeTerFuncionarios(eleitores.length)) {
        const limite = getLimiteFuncionarios();
        alert(`❌ Limite de funcionários excedido!\n\nSeu plano ${user?.tipo_usuario} permite até ${limite} funcionários.\n\nFuncionários no arquivo: ${eleitores.length}\n\nPor favor, ajuste o arquivo ou considere fazer upgrade do seu plano.`);
        return;
      }

      // Validar se o número de eleitores é exatamente igual ao número de funcionários da empresa
      if (dadosEmpresa && eleitores.length !== dadosEmpresa.numeroFuncionarios) {
        const diferenca = eleitores.length - dadosEmpresa.numeroFuncionarios;
        const mensagem = diferenca > 0 
          ? `❌ Número de eleitores EXCEDE funcionários da empresa!\n\nFuncionários cadastrados na empresa ${dadosEmpresa.nome}: ${dadosEmpresa.numeroFuncionarios}\n\nEleitores no arquivo: ${eleitores.length}\n\nExcesso: ${diferenca} eleitores\n\nPor favor, ajuste o arquivo para ter EXATAMENTE ${dadosEmpresa.numeroFuncionarios} eleitores.`
          : `❌ Número de eleitores é MENOR que funcionários da empresa!\n\nFuncionários cadastrados na empresa ${dadosEmpresa.nome}: ${dadosEmpresa.numeroFuncionarios}\n\nEleitores no arquivo: ${eleitores.length}\n\nFaltam: ${Math.abs(diferenca)} eleitores\n\nPor favor, ajuste o arquivo para ter EXATAMENTE ${dadosEmpresa.numeroFuncionarios} eleitores.`;
        
        alert(mensagem);
        return;
      }

      const confirmar = window.confirm(
        `✅ VALIDAÇÃO APROVADA!\n\n📋 Foram encontrados ${eleitores.length} eleitores válidos.\n\n✅ Número de eleitores = Número de funcionários da empresa\n✅ Limite do plano ${user?.tipo_usuario}: ${getLimiteFuncionarios()} funcionários\n\n${dadosEmpresa ? `🏢 Empresa: ${dadosEmpresa.nome}\n👥 Funcionários cadastrados: ${dadosEmpresa.numeroFuncionarios}\n🗳️ Eleitores no arquivo: ${eleitores.length}` : ''}\n\nDeseja prosseguir com o cadastro dos eleitores?`
      );

      if (confirmar) {
        await salvarEleitoresComEleicao(eleitores, eleicaoParaUsar);
        
        setArquivoCsv(null);
        // Limpar input file
        const fileInput = document.getElementById('csv-eleitores') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      alert(`❌ Erro ao processar CSV: ${errorMessage}`);
    } finally {
      setProcessandoCsv(false);
    }
  };

  // Função para calcular votos do histórico
  const calcularVotosHistorico = async () => {
    if (!eleicoes.length) return;

    const votosMap: {[key: string]: number} = {};

    try {
      // Buscar todos os votos de uma vez usando IN
      const eleicaoIds = eleicoes.map(e => e.id);
      const { data: votos, error } = await supabase
        .from('cp_votos')
        .select('eleicao_id')
        .in('eleicao_id', eleicaoIds);

      if (error) {
        console.warn('⚠️ Erro ao buscar votos:', error);
        // Fallback: inicializar com 0 para todas as eleições
        eleicoes.forEach(eleicao => {
          votosMap[eleicao.id] = 0;
        });
      } else {
        // Contar votos por eleição
        eleicoes.forEach(eleicao => {
          votosMap[eleicao.id] = votos?.filter(v => v.eleicao_id === eleicao.id).length || 0;
        });
      }
    } catch (error) {
      console.warn('⚠️ Erro ao calcular votos do histórico:', error);
      // Fallback: inicializar com 0 para todas as eleições
      eleicoes.forEach(eleicao => {
        votosMap[eleicao.id] = 0;
      });
    }

    setVotosHistorico(votosMap);
  };

  // useEffect para carregar dados da empresa
  React.useEffect(() => {
    if (empresaSelecionada) {
      carregarDadosEmpresa();
    }
  }, [empresaSelecionada]);

  // useEffect para preencher automaticamente nome da empresa e slug
  React.useEffect(() => {
    if (empresaSelecionada && empresaSelecionada.nome) {
      // Preencher nome da empresa automaticamente
      setNomeEmpresa(empresaSelecionada.nome);
      
      // Gerar slug automaticamente
      const slugGerado = generateEmpresaSlug(empresaSelecionada.nome);
      setSlugEmpresa(slugGerado);
      
      console.log('🔄 [AUTO-FILL] Preenchendo automaticamente:', {
        nomeEmpresa: empresaSelecionada.nome,
        slugGerado: slugGerado
      });
    }
  }, [empresaSelecionada]);

  // useEffect para recarregar dados quando a aba for acessada
  React.useEffect(() => {
    if (empresaSelecionada) {
      // Recarrega os dados da empresa quando a aba de votação é acessada
      const timer = setTimeout(() => {
        console.log('🔄 Recarregando dados da empresa na aba de votação...');
        carregarDadosEmpresa();
      }, 1000); // Aguarda 1 segundo para garantir que a aba está carregada

      return () => clearTimeout(timer);
    }
  }, []);

  // useEffect para calcular votos do histórico quando eleições carregarem
  React.useEffect(() => {
    if (eleicoes.length > 0) {
      const currentKey = `votos-historico-${eleicoes.length}-${eleicoes.map(e => e.id).join(',')}`;
      
      if (lastVotosHistoricoRef.current !== currentKey) {
        lastVotosHistoricoRef.current = currentKey;
        calcularVotosHistorico();
      }
    }
  }, [eleicoes]);

  // useEffect para carregar eleitores quando eleição ativa ou em configuração mudar
  React.useEffect(() => {
    const currentKey = `eleitores-${eleicaoAtiva?.id || 'null'}-${eleicaoConfiguração?.id || 'null'}`;
    
    if (eleicaoAtiva || eleicaoConfiguração) {
      if (lastEleitoresRef.current !== currentKey) {
        lastEleitoresRef.current = currentKey;
        carregarEleitoresExistentes();
      }
    } else {
      setEleitoresAutorizados([]);
    }
  }, [eleicaoAtiva, eleicaoConfiguração]);

  // useEffect para carregar candidatos existentes quando há eleição em configuração
  React.useEffect(() => {
    if (eleicaoConfiguração && candidatos.length === 0) {
      console.log('🔄 Carregando candidatos existentes da eleição em configuração:', eleicaoConfiguração.id);
      // Forçar recarregamento dos candidatos para a eleição em configuração
      carregarCandidatosExistentes();
    }
  }, [eleicaoConfiguração]);

  // useEffect para carregar estado do cadastro público
  React.useEffect(() => {
    const carregarEstadoCadastroPublico = async () => {
      const eleicaoParaUsar = eleicaoAtiva || eleicaoConfiguração;
      console.log('🔄 [CADASTRO PÚBLICO] Carregando estado para eleição:', eleicaoParaUsar);
      
      if (eleicaoParaUsar) {
        try {
          const { data: eleicao, error } = await supabase
            .from('cp_eleicoes')
            .select('cadastro_publico_habilitado')
            .eq('id', eleicaoParaUsar.id)
            .single();

          console.log('🔄 [CADASTRO PÚBLICO] Resultado da consulta:', { eleicao, error });

          if (!error && eleicao) {
            setCadastroPublicoHabilitado(eleicao.cadastro_publico_habilitado || false);
            console.log('✅ [CADASTRO PÚBLICO] Estado carregado:', eleicao.cadastro_publico_habilitado);
          }
        } catch (error) {
          console.error('❌ Erro ao carregar estado do cadastro público:', error);
        }
      } else {
        console.log('⚠️ [CADASTRO PÚBLICO] Nenhuma eleição encontrada');
        setCadastroPublicoHabilitado(false);
      }
    };

    carregarEstadoCadastroPublico();
  }, [eleicaoAtiva, eleicaoConfiguração]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Carregando sistema de votação...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
          <p className="text-red-600 mb-4">Erro ao carregar dados: {error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* CONFIGURAÇÃO DA ELEIÇÃO - PRIMEIRO */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Settings className="w-8 h-8 text-yellow-600" />
            <h4 className="text-xl font-bold text-yellow-800">Configuração da Eleição</h4>
          </div>
          
          {/* Informação sobre preenchimento automático */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-green-600">✅</span>
              <p className="text-green-800 font-medium">Preenchimento Automático Ativado</p>
            </div>
            <p className="text-green-700 text-sm">
              O nome da empresa e slug são preenchidos automaticamente com base na empresa selecionada. 
              Isso facilita a criação da eleição e garante URLs consistentes.
            </p>
          </div>
          
          <div className="space-y-3">
            <div>
              <label htmlFor="titulo-eleicao" className="block text-sm font-medium text-gray-700 mb-1">
                Título da Eleição
              </label>
              <input
                id="titulo-eleicao"
                name="titulo-eleicao"
                type="text"
                value={tituloEleicao}
                onChange={(e) => setTituloEleicao(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Ex: Eleição CIPA - Gestão 2025/2026"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label htmlFor="nome-empresa" className="block text-sm font-medium text-gray-700 mb-1">
                  Nome da Empresa * (Preenchido Automaticamente)
                </label>
                <div className="relative">
                  <input
                    id="nome-empresa"
                    name="nome-empresa"
                    type="text"
                    value={nomeEmpresa}
                    onChange={(e) => {
                      setNomeEmpresa(e.target.value);
                      // Gerar slug automaticamente
                      if (e.target.value.trim()) {
                        const novoSlug = generateEmpresaSlug(e.target.value);
                        setSlugEmpresa(novoSlug);
                      }
                    }}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Ex: Empresa Exemplo Ltda"
                    required
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                    <span className="text-xs text-green-600 font-medium">Auto</span>
                  </div>
                </div>
                <p className="text-xs text-green-600 mt-1">
                  ✅ Preenchido automaticamente com o nome da empresa selecionada
                </p>
              </div>
              <div>
                <label htmlFor="slug-empresa" className="block text-sm font-medium text-gray-700 mb-1">
                  Slug da Empresa (URL) - Gerado Automaticamente
                </label>
                <div className="relative">
                  <input
                    id="slug-empresa"
                    name="slug-empresa"
                    type="text"
                    value={slugEmpresa}
                    readOnly
                    className="w-full px-3 py-2 border rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                    placeholder="Será gerado automaticamente"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                    <span className="text-xs text-gray-500">Auto</span>
                  </div>
                </div>
                <p className="text-xs text-gray-600 mt-1">
                  URL será: https://cipa.ehsfacil.com/{slugEmpresa || 'empresa'}
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  💡 O slug é gerado automaticamente baseado no nome da empresa + ano atual
                </p>
              </div>
            </div>
            <div>
              <label htmlFor="senha-sistema" className="block text-sm font-medium text-gray-700 mb-1">
                Senha Única para Votação
              </label>
              <input
                id="senha-sistema"
                name="senha-sistema"
                type="password"
                value={senhaSistema}
                onChange={(e) => setSenhaSistema(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Senha que será usada por todos os eleitores"
              />
              <p className="text-xs text-gray-600 mt-1">
                Esta senha será compartilhada com todos os eleitores para garantir acesso autorizado
              </p>
            </div>
          </div>
          
          {/* Botões de ação */}
          <div className="flex gap-3 justify-center mt-6">
            <button
              onClick={async () => {
                try {
                  setSubmitting(true);
                  
                  // Debug: Verificar valores antes de criar
                  console.log('🔍 [BOTÃO CRIAR] Valores antes de criar eleição:', {
                    user: user,
                    user_id: user?.id,
                    empresaSelecionada: empresaSelecionada,
                    empresa_id: empresaSelecionada?.id
                  });
                  
                  // Criar eleição em modo configuração
                  const eleicaoCriada = await criarEleicaoNova();
                  
                  if (eleicaoCriada) {
                    alert('✅ Eleição criada com sucesso!\n\nAgora você pode:\n1. ✅ Cadastro público já habilitado\n2. Adicionar candidatos manualmente\n3. Configurar eleitores\n4. Iniciar a votação quando estiver pronto\n\n🌐 Link público: https://cipa.ehsfacil.com/candidato/' + eleicaoCriada.empresa_slug + '\n\n📋 A eleição está em modo configuração. Use o botão "Iniciar Votação" quando estiver pronto.');
                  }
                } catch (error) {
                  console.error('❌ Erro ao criar eleição:', error);
                  alert('❌ Erro ao criar eleição. Tente novamente.');
                } finally {
                  setSubmitting(false);
                }
              }}
              disabled={submitting || !nomeEmpresa || !slugEmpresa}
              className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Calendar className="w-4 h-4" />
              )}
              Criar Eleição
            </button>
          </div>
        </div>
      </div>

      {/* CONTROLE DE CADASTRO PÚBLICO - SEGUNDO */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Users className="w-8 h-8 text-purple-600" />
            <h4 className="text-xl font-bold text-purple-800">Cadastro Público de Candidatos</h4>
          </div>
          
          {/* Controle de Habilitar/Desabilitar */}
          <div className="bg-white border border-purple-200 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between mb-4">
              <div className="text-left">
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-3 h-3 rounded-full ${cadastroPublicoHabilitado ? 'bg-green-500' : 'bg-red-500'}`}></div>
                  <p className="text-purple-800 font-medium">
                    {cadastroPublicoHabilitado ? '✅ Cadastro Público Habilitado' : '🔒 Cadastro Público Desabilitado'}
                  </p>
                </div>
                <p className="text-purple-700 text-sm">
                  {cadastroPublicoHabilitado 
                    ? 'Os candidatos podem se cadastrar usando o link público abaixo.'
                    : 'Apenas o administrador pode adicionar candidatos. Ative para permitir cadastro público.'
                  }
                </p>
                {(eleicaoAtiva || eleicaoConfiguração) ? (
                  <p className="text-xs text-gray-500 mt-1">
                    Eleição: {(eleicaoAtiva || eleicaoConfiguração)?.titulo || 'Sem título'}
                  </p>
                ) : (
                  <div className="text-xs text-red-500 mt-1">
                    <p>⚠️ Nenhuma eleição encontrada. Crie uma eleição primeiro.</p>
                    <p className="text-green-600 mt-1">
                      💡 Dica: Use o botão "Criar Eleição (Ativa)" na seção de configuração acima.
                    </p>
                  </div>
                )}
              </div>
              <button
                onClick={handleToggleCadastroPublico}
                className={`px-6 py-3 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                  cadastroPublicoHabilitado
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'bg-green-600 text-white hover:bg-green-700'
                }`}
              >
                {cadastroPublicoHabilitado ? (
                  <>
                    <span>🔒</span>
                    Desabilitar
                  </>
                ) : (
                  <>
                    <span>🔓</span>
                    Habilitar
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Debug info */}
          {process.env.NODE_ENV === 'development' && (
            <div className="bg-gray-100 border border-gray-300 rounded-lg p-3 mb-4 text-xs">
              <p><strong>Debug:</strong></p>
              <p>cadastroPublicoHabilitado: {String(cadastroPublicoHabilitado)}</p>
              <p>eleicaoAtiva: {eleicaoAtiva ? 'Sim' : 'Não'}</p>
              <p>eleicaoConfiguração: {eleicaoConfiguração ? 'Sim' : 'Não'}</p>
              <p>Mostrar link: {String(cadastroPublicoHabilitado && (eleicaoAtiva || eleicaoConfiguração))}</p>
            </div>
          )}

          {/* Link Público - Mostra se habilitado e há eleição (ativa ou em configuração) */}
          {cadastroPublicoHabilitado && (eleicaoAtiva || eleicaoConfiguração) && (
            <div className="bg-white border border-purple-200 rounded-lg p-4 mb-4">
              <p className="text-purple-800 font-medium mb-2">
                🎯 Link para Candidatos se Cadastrarem
              </p>
              <p className="text-purple-700 text-sm mb-3">
                Compartilhe este link com os funcionários que desejam se candidatar à eleição CIPA.
              </p>
              
              <div className="flex items-center justify-center gap-2 mb-3">
                <code className="bg-gray-100 px-3 py-2 rounded text-sm font-mono">
                  https://cipa.ehsfacil.com/candidato/{(eleicaoAtiva || eleicaoConfiguração)?.empresa_slug || 'sua-empresa'}
                </code>
                <button
                  onClick={() => navigator.clipboard.writeText(`https://cipa.ehsfacil.com/candidato/${(eleicaoAtiva || eleicaoConfiguração)?.empresa_slug || 'sua-empresa'}`)}
                  className="bg-purple-600 text-white px-3 py-2 rounded text-sm hover:bg-purple-700 transition-colors"
                  title="Copiar link"
                >
                  Copiar
                </button>
              </div>
              
              <div className="text-xs text-purple-600">
                <p className="font-medium mb-1">📋 O que os candidatos precisam informar:</p>
                <ul className="text-left space-y-1">
                  <li>• Dados pessoais (nome, email, telefone)</li>
                  <li>• Dados profissionais (matrícula, setor, cargo)</li>
                  <li>• Endereço completo</li>
                  <li>• Foto (opcional)</li>
                  <li>• Observações adicionais</li>
                </ul>
              </div>
            </div>
          )}

          {/* Slug da Empresa - Visível quando há eleição (ativa ou em configuração) */}
          {(eleicaoAtiva || eleicaoConfiguração) && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-blue-800 font-medium mb-2">
                🔗 Slug da Empresa (URL)
              </p>
              <div className="flex items-center justify-center gap-2">
                <code className="bg-gray-100 px-3 py-2 rounded text-sm font-mono">
                  {(eleicaoAtiva || eleicaoConfiguração)?.empresa_slug || 'sua-empresa'}
                </code>
                <button
                  onClick={() => navigator.clipboard.writeText((eleicaoAtiva || eleicaoConfiguração)?.empresa_slug || 'sua-empresa')}
                  className="bg-blue-600 text-white px-3 py-2 rounded text-sm hover:bg-blue-700 transition-colors"
                  title="Copiar slug"
                >
                  Copiar
                </button>
              </div>
              <p className="text-blue-700 text-xs mt-2">
                URL completa: https://cipa.ehsfacil.com/{(eleicaoAtiva || eleicaoConfiguração)?.empresa_slug || 'sua-empresa'}
              </p>
            </div>
          )}


          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-yellow-800 text-sm">
              <strong>ℹ️ Informação:</strong> {cadastroPublicoHabilitado 
                ? 'O sistema validará automaticamente se a matrícula já está cadastrada para evitar duplicatas.'
                : 'Para permitir que candidatos se cadastrem publicamente, habilite o cadastro público acima.'
              }
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-3">
          <Vote className="text-blue-600" />
          Sistema de Votação Online CIPA - 100% Secreto
        </h2>

        {/* CONFIGURAÇÃO INICIAL */}
        {!eleicaoAtiva && !mostrarCadastroCandidatos && (
          <div className="text-center py-12">
            {eleicaoConfiguração && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full animate-pulse"></div>
                  <span className="font-medium text-yellow-800">Eleição em Configuração</span>
                </div>
                <p className="text-yellow-700 text-sm">
                  ID: {eleicaoConfiguração.id} | Status: Configurando
                </p>
                <p className="text-yellow-600 text-xs mt-1">
                  Você pode adicionar candidatos e eleitores. Clique em "Configurar Candidatos" para continuar.
                </p>
                {candidatos.length > 0 && (
                  <div className="mt-3 p-2 bg-white bg-opacity-50 rounded border">
                    <p className="text-green-700 text-sm font-medium">
                      ✅ {candidatos.length} candidatos já cadastrados
                    </p>
                    <p className="text-green-600 text-xs">
                      Os candidatos existentes serão mantidos ao continuar a configuração.
                    </p>
                  </div>
                )}
              </div>
            )}
            
            <Vote className="w-24 h-24 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-4">Configure a Votação</h3>
            <p className="text-gray-600 mb-6">
              {eleicaoConfiguração 
                ? 'Continue configurando sua eleição' 
                : 'Primeiro, cadastre os candidatos e defina a senha do sistema'
              }
            </p>
            <button
              onClick={() => setMostrarCadastroCandidatos(true)}
              className={`px-6 py-3 rounded-lg transition-colors font-medium ${
                candidatos.length > 0 
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {eleicaoConfiguração 
                ? (candidatos.length > 0 
                    ? `Continuar Configuração (${candidatos.length} candidatos)`
                    : 'Continuar Configuração')
                : 'Configurar Candidatos'
              }
            </button>
          </div>
        )}

        {/* CADASTRO DE CANDIDATOS */}
        {mostrarCadastroCandidatos && !eleicaoAtiva && (
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-bold text-blue-800">Cadastro de Candidatos</h3>
                <div className="text-sm text-blue-700">
                  {parametros && (
                    <span>
                      {candidatos.length} de {getLimiteEleitores()} eleitores
                      {getEleitoresRestantes(candidatos.length) !== 'Ilimitado' && (
                        <span className="ml-1">
                          ({getEleitoresRestantes(candidatos.length)} restantes)
                        </span>
                      )}
                    </span>
                  )}
                </div>
              </div>
              
              {candidatos.length > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="font-medium text-green-800">
                        ✅ {candidatos.length} candidatos já cadastrados
                      </span>
                    </div>
                    <button
                      onClick={handleLimparCandidatosDuplicados}
                      disabled={submitting}
                      className="flex items-center gap-1 px-3 py-1 bg-orange-600 text-white rounded text-sm hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      title="Limpar candidatos duplicados"
                    >
                      <Trash2 className="w-4 h-4" />
                      {submitting ? 'Limpando...' : 'Limpar Duplicados'}
                    </button>
                  </div>
                  <p className="text-green-700 text-sm mt-1">
                    Os candidatos existentes são mantidos. Você pode adicionar novos candidatos ou editar os existentes.
                  </p>
                </div>
              )}
              
              {!podeAdicionarEleitor(candidatos.length) && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-yellow-600" />
                    <p className="text-yellow-800">
                      Você atingiu o limite máximo de eleitores para seu perfil ({getLimiteEleitores()}).
                      Entre em contato com o administrador para aumentar seu limite.
                    </p>
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Informações do Candidato */}
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label htmlFor="nome-candidato" className="block text-sm font-medium text-gray-700 mb-1">
                        Nome Completo *
                      </label>
                      <input
                        id="nome-candidato"
                        name="nome-candidato"
                        type="text"
                        placeholder="Nome completo"
                        value={novoCandidato.nome}
                        onChange={(e) => setNovoCandidato({...novoCandidato, nome: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="matricula-candidato" className="block text-sm font-medium text-gray-700 mb-1">
                        Matrícula *
                      </label>
                      <input
                        id="matricula-candidato"
                        name="matricula-candidato"
                        type="text"
                        placeholder="Matrícula"
                        value={novoCandidato.matricula}
                        onChange={(e) => setNovoCandidato({...novoCandidato, matricula: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="setor-candidato" className="block text-sm font-medium text-gray-700 mb-1">
                        Setor *
                      </label>
                      <input
                        id="setor-candidato"
                        name="setor-candidato"
                        type="text"
                        placeholder="Setor"
                        value={novoCandidato.setor}
                        onChange={(e) => setNovoCandidato({...novoCandidato, setor: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <button
                    onClick={handleAdicionarCandidato}
                    disabled={submitting || !podeAdicionarEleitor(candidatos.length)}
                    className={`w-full px-4 py-2 rounded-lg flex items-center justify-center gap-2 ${
                      podeAdicionarEleitor(candidatos.length)
                        ? 'bg-green-600 text-white hover:bg-green-700'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {submitting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Plus className="w-4 h-4" />
                    )}
                    Adicionar Candidato
                  </button>
                </div>

                {/* Upload de Foto */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Foto do Candidato (Opcional)
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
                      {previewFoto ? (
                        <div className="space-y-3">
                          <img 
                            src={previewFoto} 
                            alt="Preview da foto" 
                            className="w-32 h-32 object-cover rounded-lg mx-auto"
                          />
                          <div className="flex gap-2 justify-center">
                            <button
                              onClick={handleRemoverFoto}
                              className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
                            >
                              Remover
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <Camera className="w-12 h-12 text-gray-400 mx-auto" />
                          <div>
                            <label htmlFor="foto-candidato" className="cursor-pointer">
                              <span className="text-blue-600 hover:text-blue-700 font-medium">
                                Clique para selecionar uma foto
                              </span>
                              <p className="text-sm text-gray-500 mt-1">
                                JPG, PNG até 5MB
                              </p>
                            </label>
                            <input
                              id="foto-candidato"
                              type="file"
                              accept="image/*"
                              onChange={handleFotoChange}
                              className="hidden"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {candidatos.length > 0 && (
                <div className="bg-white rounded-lg p-4 mt-4 max-h-96 overflow-y-auto">
                  <h4 className="font-medium text-gray-700 mb-3">Candidatos cadastrados ({candidatos.length}):</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {candidatos.map(candidato => (
                      <div key={candidato.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <div className="flex-shrink-0">
                          {(() => {
                            const fotoUrl = obterFotoCandidato(candidato);
                            return fotoUrl ? (
                              <img 
                                src={fotoUrl} 
                                alt={candidato.nome}
                                className="w-12 h-12 object-cover rounded-full"
                              />
                            ) : (
                              <div className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center">
                                <Users className="w-6 h-6 text-gray-500" />
                              </div>
                            );
                          })()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{candidato.nome}</p>
                          <p className="text-xs text-gray-600">
                            Mat. {candidato.matricula} - {candidato.setor}
                          </p>
                        </div>
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleAtualizarFoto(candidato.id)}
                            className="text-blue-600 hover:text-blue-800 p-1"
                            title="Atualizar foto"
                          >
                            <Upload className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleRemoverCandidato(candidato.id)}
                            className="text-red-600 hover:text-red-800 p-1"
                            title="Remover candidato"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className={`border rounded-lg p-4 mb-4 ${
              eleitoresAutorizados.length > 0 
                ? 'bg-green-50 border-green-200' 
                : 'bg-red-50 border-red-200'
            }`}>
              <h3 className={`font-bold mb-3 flex items-center gap-2 ${
                eleitoresAutorizados.length > 0 
                  ? 'text-green-800' 
                  : 'text-red-800'
              }`}>
                <Upload className="w-5 h-5" />
                ⚠️ Upload de Eleitores Autorizados (OBRIGATÓRIO)
                {carregandoEleitores && (
                  <Loader2 className="w-4 h-4 animate-spin ml-2" />
                )}
              </h3>
              
              {eleitoresAutorizados.length === 0 && (
                <div className="bg-white border border-red-300 rounded-lg p-3 mb-3">
                  <p className="text-red-700 font-medium mb-1">
                    🚫 Nenhum eleitor autorizado cadastrado
                  </p>
                  <p className="text-red-600 text-sm">
                    É obrigatório fazer upload de um arquivo CSV com os CPFs dos colaboradores 
                    autorizados a votar antes de iniciar a votação.
                  </p>
                  {dadosEmpresa && (
                    <p className="text-red-600 text-sm mt-2 font-medium">
                      ⚠️ O arquivo deve conter EXATAMENTE {dadosEmpresa.numeroFuncionarios} eleitores.
                    </p>
                  )}
                </div>
              )}
              
              {eleitoresAutorizados.length > 0 && dadosEmpresa && (
                <div className="mb-3">
                  <ValidacaoEleitores
                    eleitoresCadastrados={eleitoresAutorizados.length}
                    funcionariosEmpresa={dadosEmpresa.numeroFuncionarios}
                    nomeEmpresa={dadosEmpresa.nome}
                  />
                </div>
              )}
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Arquivo CSV com CPFs dos colaboradores autorizados
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      id="csv-eleitores"
                      type="file"
                      accept=".csv"
                      onChange={handleCsvChange}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                    <button
                      onClick={handleProcessarCsv}
                      disabled={!arquivoCsv || processandoCsv}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {processandoCsv ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4" />
                      )}
                      {processandoCsv ? 'Processando...' : 'Processar CSV'}
                    </button>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <div>
                      <p className="text-xs text-gray-600">
                        <strong>Formato esperado:</strong> CPF, Nome, Setor (primeira linha como cabeçalho)
                      </p>
                      {parametros && (
                        <p className="text-xs text-blue-600 mt-1">
                          <strong>Limite do plano {user?.tipo_usuario}:</strong> {getLimiteFuncionarios()} funcionários
                        </p>
                      )}
                      {dadosEmpresa && (
                        <div className="mt-1">
                          <p className="text-xs text-green-600">
                            <strong>Funcionários da empresa {dadosEmpresa.nome}:</strong> {dadosEmpresa.numeroFuncionarios}
                          </p>
                          <p className="text-xs text-red-600 font-medium">
                            ⚠️ O arquivo deve ter EXATAMENTE {dadosEmpresa.numeroFuncionarios} eleitores (nem mais, nem menos)
                          </p>
                        </div>
                      )}
                      <p className="text-xs text-gray-600">
                        <strong>Exemplo:</strong> cpf,nome,setor<br/>
                        12345678901,João Silva,TI<br/>
                        98765432100,Maria Santos,RH
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        // Criar conteúdo CSV de exemplo
                        const csvContent = `cpf,nome,setor
                                            05612998031,João Silva,Tecnologia da Informação
                                            19865074087,Maria Santos,Recursos Humanos
                                            47740203033,Pedro Oliveira,Financeiro
                                            19380843062,Ana Costa,Operações
                                            34598249056,Carlos Ferreira,Manutenção
                                            79294838005,Lucia Pereira,Administrativo`;

                        // Criar e baixar arquivo com codificação UTF-8 correta
                        const BOM = '\uFEFF'; // Byte Order Mark para UTF-8
                        const csvWithBOM = BOM + csvContent;
                        const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' });
                        const link = document.createElement('a');
                        const url = URL.createObjectURL(blob);
                        link.setAttribute('href', url);
                        link.setAttribute('download', 'exemplo_eleitores_autorizados.csv');
                        link.style.visibility = 'hidden';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      }}
                      className="bg-blue-600 text-white px-3 py-1 rounded text-xs hover:bg-blue-700 transition-colors flex items-center gap-1"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Baixar Exemplo
                    </button>
                  </div>
                </div>
                
                {eleitoresAutorizados.length > 0 && (
                  <div className="bg-white p-3 rounded border">
                    <p className="text-sm font-medium text-green-700 mb-2">
                      ✅ {eleitoresAutorizados.length} eleitores autorizados cadastrados
                    </p>
                    <div className="max-h-32 overflow-y-auto">
                      <div className="text-xs text-gray-600">
                        {eleitoresAutorizados.slice(0, 5).map((eleitor, index) => (
                          <div key={index}>
                            {eleitor.cpf} - {eleitor.nome || 'Sem nome'} - {eleitor.setor || 'Sem setor'}
                          </div>
                        ))}
                        {eleitoresAutorizados.length > 5 && (
                          <div className="text-gray-500">... e mais {eleitoresAutorizados.length - 5} eleitores</div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>


            <div className="flex gap-3 justify-center">
              <button
                onClick={() => setMostrarCadastroCandidatos(false)}
                className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700"
              >
                Cancelar
              </button>
              
              <button
                onClick={handleIniciarVotacao}
                disabled={submitting || candidatos.length < 2 || !senhaSistema || !tituloEleicao || !nomeEmpresa || !slugEmpresa || eleitoresAutorizados.length === 0 || (dadosEmpresa ? eleitoresAutorizados.length !== dadosEmpresa.numeroFuncionarios : true)}
                className={`px-6 py-2 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 ${
                  dadosEmpresa && eleitoresAutorizados.length === dadosEmpresa.numeroFuncionarios
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Calendar className="w-4 h-4" />
                )}
                {dadosEmpresa && eleitoresAutorizados.length === dadosEmpresa.numeroFuncionarios
                  ? '✅ Iniciar Votação (Validação Aprovada)'
                  : 'Iniciar Votação'
                }
              </button>
            </div>
          </div>
        )}


        {/* VOTAÇÃO ATIVA */}
        {eleicaoAtiva && (
          <div className="space-y-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="font-medium text-green-800">Votação em Andamento</span>
                </div>
                <button
                  onClick={handleEncerrarVotacao}
                  disabled={processandoApuracao}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm flex items-center gap-2"
                >
                  {processandoApuracao ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Processando...
                    </>
                  ) : (
                    'Encerrar Votação'
                  )}
                </button>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Total de Votos:</span>
                  <span className="ml-2 font-bold">{eleicaoAtiva.total_votos}</span>
                </div>
                <div>
                  <span className="text-gray-600">Participação:</span>
                  <span className={`ml-2 font-bold ${
                    eleitoresAutorizados.length > 0 && (eleicaoAtiva.total_votos / eleitoresAutorizados.length) * 100 >= 51 
                      ? 'text-green-600' 
                      : 'text-yellow-600'
                  }`}>
                    {eleitoresAutorizados.length > 0 
                      ? `${((eleicaoAtiva.total_votos / eleitoresAutorizados.length) * 100).toFixed(1)}%`
                      : '0%'
                    }
                  </span>
                </div>
              </div>
              
              {/* Barra de progresso da participação */}
              {eleitoresAutorizados.length > 0 && (
                <div className="mt-3">
                  <div className="flex justify-between text-xs text-gray-600 mb-1">
                    <span>Participação (mínimo 51%)</span>
                    <span>{eleicaoAtiva.total_votos} / {eleitoresAutorizados.length} eleitores</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all duration-300 ${
                        (eleicaoAtiva.total_votos / eleitoresAutorizados.length) * 100 >= 51 
                          ? 'bg-green-500' 
                          : 'bg-yellow-500'
                      }`}
                      style={{
                        width: `${Math.min(100, (eleicaoAtiva.total_votos / eleitoresAutorizados.length) * 100)}%`
                      }}
                    ></div>
                  </div>
                  {(eleicaoAtiva.total_votos / eleitoresAutorizados.length) * 100 < 51 && (
                    <p className="text-xs text-yellow-600 mt-1">
                      ⚠️ Faltam {Math.ceil((51 / 100) * eleitoresAutorizados.length) - eleicaoAtiva.total_votos} votos para atingir 51%
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h3 className="font-bold text-yellow-800 mb-2 flex items-center gap-2">
                <Shield className="w-5 h-5" />
                Sistema de Voto Secreto
              </h3>
              <p className="text-sm text-yellow-700">
                O CPF é usado apenas para evitar duplicação. Não há registro de qual candidato cada eleitor votou.
                O voto é 100% secreto e anônimo conforme exigido pela NR-05.
              </p>
            </div>

            {/* INFORMAÇÕES DA VOTAÇÃO */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-bold text-blue-800 mb-3 flex items-center gap-2">
                <Vote className="w-5 h-5" />
                URL Pública da Votação
              </h3>
              <div className="bg-white p-3 rounded border">
                <p className="text-sm text-gray-600 mb-2">Os eleitores devem acessar:</p>
                <div className="flex items-center gap-2">
                  <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">
                    https://cipa.ehsfacil.com/{eleicaoAtiva.empresa_slug || 'sua-empresa'}
                  </code>
                  <button
                    onClick={() => navigator.clipboard.writeText(`https://cipa.ehsfacil.com/${eleicaoAtiva.empresa_slug || 'sua-empresa'}`)}
                    className="text-blue-600 hover:text-blue-800 text-sm"
                  >
                    Copiar
                  </button>
                </div>
              </div>
            </div>

            {/* INFORMAÇÕES DA VOTAÇÃO - SEM MONITORAMENTO EM TEMPO REAL */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <Shield className="w-8 h-8 text-green-600" />
                  <h4 className="text-xl font-bold text-gray-800">Votação em Andamento</h4>
                </div>
                
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <p className="text-green-800 font-medium mb-2">
                    🔒 Votação Secreta em Andamento
                  </p>
                  <p className="text-green-700 text-sm">
                    A votação está ocorrendo de forma secreta e segura. Os resultados serão divulgados apenas após o encerramento.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <span className="text-gray-600">Total de Candidatos:</span>
                    <span className="ml-2 font-bold text-blue-600">{candidatos.length}</span>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <span className="text-gray-600">Total de Eleitores:</span>
                    <span className="ml-2 font-bold text-green-600">{eleitoresAutorizados.length}</span>
                  </div>
                </div>

                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-blue-800 text-sm">
                    <strong>ℹ️ Informação:</strong> Para garantir o sigilo da votação, os resultados não são exibidos em tempo real. 
                    A apuração será realizada automaticamente após o encerramento da votação.
                  </p>
                </div>
              </div>
            </div>

          </div>
        )}

        {/* HISTÓRICO DE ELEIÇÕES */}
        {eleicoes.length > 0 && (
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Award className="w-5 h-5 text-purple-600" />
              Histórico de Eleições
            </h3>
            <div className="space-y-3">
              {eleicoes.map(eleicao => {
                const isConfigurando = eleicao.status === 'configurando';
                const isAtiva = eleicao.status === 'ativa';
                const isEncerrada = eleicao.status === 'encerrada';
                const totalVotos = votosHistorico[eleicao.id] ?? eleicao.total_votos ?? 0;
                
                return (
                  <div key={eleicao.id} className={`p-4 rounded-lg border ${
                    isConfigurando ? 'bg-yellow-50 border-yellow-200' :
                    isAtiva ? 'bg-green-50 border-green-200' :
                    'bg-gray-50 border-gray-200'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium text-gray-800">{eleicao.titulo}</h4>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            isAtiva ? 'bg-green-100 text-green-800' :
                            isEncerrada ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {isAtiva ? '🟢 Ativa' :
                             isEncerrada ? '🔴 Encerrada' : '🟡 Configurando'}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600 space-y-1">
                          <p>📅 Criada em {new Date(eleicao.created_at).toLocaleDateString('pt-BR')}</p>
                          {eleicao.empresa_nome && (
                            <p>🏢 Empresa: {eleicao.empresa_nome}</p>
                          )}
                          {eleicao.empresa_slug && (
                            <p>🔗 URL: https://cipa.ehsfacil.com/{eleicao.empresa_slug}</p>
                          )}
                          {!isConfigurando && (
                            <p>🗳️ Total de votos: {totalVotos}</p>
                          )}
                        </div>
                      </div>
                      
                      {/* Ações para eleições em configuração */}
                      {isConfigurando && (
                        <div className="flex items-center gap-2 ml-4">
                          <button
                            onClick={() => editarEleicaoConfiguracao(eleicao)}
                            className="flex items-center gap-1 px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors"
                            title="Editar eleição"
                          >
                            <Edit className="w-4 h-4" />
                            Editar
                          </button>
                          <button
                            onClick={() => handleBaixarRelatorioCandidatos(eleicao)}
                            className="flex items-center gap-1 px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 transition-colors"
                            title="Baixar relatório de candidatos"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            Candidatos
                          </button>
                          <button
                            onClick={() => excluirEleicaoConfiguracao(eleicao)}
                            className="flex items-center gap-1 px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700 transition-colors"
                            title="Excluir eleição"
                          >
                            <Trash2 className="w-4 h-4" />
                            Excluir
                          </button>
                        </div>
                      )}
                      
                      {/* Ações para eleições ativas */}
                      {isAtiva && (
                        <div className="flex items-center gap-2 ml-4">
                          <button
                            onClick={() => handleBaixarRelatorioCandidatos(eleicao)}
                            className="flex items-center gap-1 px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 transition-colors"
                            title="Baixar relatório de candidatos"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            Candidatos
                          </button>
                          <span className="text-xs text-green-600 font-medium">
                            ✅ Votação em andamento
                          </span>
                        </div>
                      )}
                      
                      {/* Ações para eleições encerradas */}
                      {isEncerrada && (
                        <div className="flex items-center gap-2 ml-4">
                          <button
                            onClick={() => handleVisualizarApuracao(eleicao)}
                            disabled={processandoApuracao}
                            className="flex items-center gap-1 px-3 py-1 bg-purple-600 text-white rounded text-sm hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            title="Visualizar apuração da eleição"
                          >
                            {processandoApuracao ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Award className="w-4 h-4" />
                            )}
                            {processandoApuracao ? 'Carregando...' : 'Ver Apuração'}
                          </button>
                          <button
                            onClick={() => handleBaixarRelatorioCandidatos(eleicao)}
                            className="flex items-center gap-1 px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 transition-colors"
                            title="Baixar relatório de candidatos"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            Candidatos
                          </button>
                          <span className="text-xs text-gray-600">
                            📊 Histórico
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Informações adicionais */}
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-2">
                <Settings className="w-5 h-5 text-blue-600 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">💡 Informações sobre o histórico:</p>
                  <ul className="space-y-1 text-xs">
                    <li>• <strong>Configurando:</strong> Eleições que ainda não foram iniciadas - podem ser editadas, excluídas ou ter relatório de candidatos baixado</li>
                    <li>• <strong>Ativa:</strong> Eleições em andamento - podem ter relatório de candidatos baixado</li>
                    <li>• <strong>Encerrada:</strong> Eleições finalizadas - clique em "Ver Apuração" para resultados ou "Candidatos" para relatório</li>
                  </ul>
                  <p className="text-xs text-blue-700 mt-2">
                    📊 <strong>Relatório de Candidatos:</strong> Contém dados completos de todos os candidatos inscritos na eleição
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* MODAL DE APURAÇÃO */}
        {mostrarModalApuracao && resultadoApuracao && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
            <div className="bg-white rounded-lg p-6 shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                  <Award className="w-8 h-8 text-yellow-600" />
                  🏆 Resultado da Apuração
                </h2>
                <button
                  onClick={fecharModalApuracao}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Vencedor */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-3 mb-4">
                    <Award className="w-12 h-12 text-yellow-600" />
                    <div>
                      <h3 className="text-2xl font-bold text-yellow-800">VENCEDOR</h3>
                      <p className="text-yellow-700">Eleição CIPA</p>
                    </div>
                  </div>
                  
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <h4 className="text-xl font-bold text-gray-800 mb-2">
                      {resultadoApuracao.vencedor.nome}
                    </h4>
                    <p className="text-gray-600 mb-1">
                      Matrícula: {resultadoApuracao.vencedor.matricula}
                    </p>
                    <p className="text-gray-600 mb-3">
                      Setor: {resultadoApuracao.vencedor.setor}
                    </p>
                    <div className="bg-yellow-100 rounded-lg p-3">
                      <p className="text-yellow-800 font-bold text-lg">
                        {resultadoApuracao.vencedor.votos} votos
                      </p>
                      <p className="text-yellow-700 text-sm">
                        {resultadoApuracao.totalVotos > 0 
                          ? `${((resultadoApuracao.vencedor.votos / resultadoApuracao.totalVotos) * 100).toFixed(1)}% dos votos`
                          : '0% dos votos'
                        }
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Estatísticas Gerais */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {resultadoApuracao.totalVotos}
                  </div>
                  <div className="text-blue-800 text-sm">Total de Votos</div>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {resultadoApuracao.totalCandidatos}
                  </div>
                  <div className="text-green-800 text-sm">Candidatos</div>
                </div>
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-purple-600">
                    {new Date(resultadoApuracao.dataApuracao).toLocaleDateString('pt-BR')}
                  </div>
                  <div className="text-purple-800 text-sm">Data da Apuração</div>
                </div>
              </div>

              {/* Classificação Completa */}
              <div className="mb-6">
                <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-gray-600" />
                  Classificação Completa
                </h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="space-y-3">
                    {resultadoApuracao.candidatos.map((candidato: any, index: number) => {
                      const percentual = resultadoApuracao.totalVotos > 0 
                        ? ((candidato.votos / resultadoApuracao.totalVotos) * 100).toFixed(1)
                        : '0.0';
                      
                      return (
                        <div key={candidato.id} className={`flex items-center justify-between p-3 rounded-lg ${
                          index === 0 ? 'bg-yellow-100 border border-yellow-300' : 'bg-white border border-gray-200'
                        }`}>
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                              index === 0 ? 'bg-yellow-500 text-white' : 'bg-gray-300 text-gray-700'
                            }`}>
                              {index + 1}º
                            </div>
                            <div>
                              <div className="font-medium text-gray-800">{candidato.nome}</div>
                              <div className="text-sm text-gray-600">
                                {candidato.matricula} - {candidato.setor}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-bold text-gray-800">{candidato.votos} votos</div>
                            <div className="text-sm text-gray-600">{percentual}%</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Botões de Ação */}
              <div className="flex gap-3 justify-center">
                <button
                  onClick={handleBaixarRelatorio}
                  className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Baixar Relatório CSV
                </button>
                <button
                  onClick={fecharModalApuracao}
                  className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VotacaoTab;
